class RoamUnoRegionalRecommendations {
  constructor(root) {
    this.root = root;
    this.code = String(root.dataset.countryCode || '').trim().toUpperCase();
    this.grid = root.querySelector('.ru-regional-recommendations__grid');
    try { this.names = JSON.parse(root.querySelector('[data-regional-names]')?.textContent || '{}'); } catch { this.names = {}; }
    void this.render();
  }

  async render() {
    const data = window.ROAMUNO_COVERAGE;
    const entries = data?.byCountry?.[this.code];
    const plans = data?.plans;

    // Fail closed: missing or malformed coverage must never turn into an
    // "all regional products" fallback.
    if (!this.code || !Array.isArray(entries) || !plans || !this.grid) {
      this.markMissingCoverage();
      return;
    }

    const fullCoverageCandidates = entries
      .map((entry) => {
        const plan = plans[entry.handle];
        if (!plan || plan.availableForSale !== true || !Array.isArray(entry.eligibleSkus)) return null;
        const availableSkus = new Set(Array.isArray(plan.availableSkus) ? plan.availableSkus : []);
        const eligibleSkus = entry.eligibleSkus.filter((sku) => availableSkus.has(sku));
        if (eligibleSkus.length === 0 || !Array.isArray(plan.guaranteedCountryCodes)) return null;
        return { entry, plan, eligibleSkus };
      })
      .filter(Boolean);
    const exposedCandidates = window.ROAMUNO_CATALOG_ROLES?.suppressConditionalDuplicate
      ? window.ROAMUNO_CATALOG_ROLES.suppressConditionalDuplicate(fullCoverageCandidates)
      : fullCoverageCandidates;
    const candidates = exposedCandidates
      .sort((a, b) => {
        // Specific regional plans first; Global is always the last fallback.
        const globalRank = Number(Boolean(a.plan.isGlobal)) - Number(Boolean(b.plan.isGlobal));
        if (globalRank !== 0) return globalRank;
        const coverageRank = a.plan.guaranteedCountryCodes.length - b.plan.guaranteedCountryCodes.length;
        if (coverageRank !== 0) return coverageRank;
        const priceRank = Number(a.plan.price) - Number(b.plan.price);
        return priceRank || String(a.entry.handle).localeCompare(String(b.entry.handle));
      })
      .slice(0, 8);

    const quoteMinimum = window.ROAMUNO_MARKET_PRICES?.minimum;
    if (candidates.length === 0 || typeof quoteMinimum !== 'function') {
      this.markMissingCoverage();
      return;
    }

    const priced = await Promise.all(candidates.map(async ({ entry, plan, eligibleSkus }) => ({
      entry, plan, eligibleSkus,
      quote: await quoteMinimum(entry.handle, eligibleSkus),
    })));
    const available = priced.filter(candidate => candidate.quote).slice(0, 4);
    if (available.length === 0) {
      this.markMissingCoverage();
      return;
    }
    this.grid.replaceChildren(...available.map(({ entry, plan, eligibleSkus, quote }) => this.createCard(entry, plan, eligibleSkus, quote)));
    this.root.dataset.coverageState = 'ready';
    this.root.hidden = false;
  }

  createCard(entry, plan, eligibleSkus, quote) {
    const card = document.createElement('a');
    card.className = 'ru-regional-card';
    card.dataset.regionalCard = entry.handle;
    card.href = plan.url || `/products/${entry.handle}`;

    const image = document.createElement('img');
    const template = String(this.root.dataset.assetTemplate || '');
    const region = entry.handle === 'europe-33-esim' ? 'europe' : entry.handle.replace(/-esim$/, '');
    image.src = template.replace('experience-region-global.svg', `experience-region-${region}.svg`);
    image.width = 44;
    image.height = 34;
    image.loading = 'lazy';
    image.alt = '';

    const body = document.createElement('div');
    body.className = 'ru-regional-card__body';
    const title = document.createElement('h3');
    title.textContent = this.names[region] || plan.title || entry.handle;
    const note = document.createElement('p');
    const countryName = this.root.dataset.countryName || dataCountryName(this.code);
    const templateText = entry.guaranteed ? this.root.dataset.noteEvery : this.root.dataset.noteSelected;
    note.textContent = String(templateText || '')
      .replaceAll('%%COUNTRY%%', countryName)
      .replaceAll('%COUNTRY%', countryName)
      .replaceAll('%%COUNT%%', String(plan.guaranteedCountryCodes.length))
      .replaceAll('%COUNT%', String(plan.guaranteedCountryCodes.length));
    const price = document.createElement('strong');
    price.dataset.roamunoMarketPrice = '';
    price.textContent = `${this.root.dataset.commonFrom || 'From'} ${formatMoney(quote.amount, quote.currency)} ${quote.currency}`;
    body.append(title);
    if (!entry.guaranteed) body.append(note);
    body.append(price);
    card.append(image, body);
    const arrow = this.root.querySelector('[data-regional-arrow]');
    if (arrow) card.append(arrow.content.cloneNode(true));
    card.dataset.availableSkus = String(eligibleSkus.length);
    return card;
  }

  markMissingCoverage() {
    this.root.dataset.coverageState = 'missing';
    this.root.hidden = true;
    this.grid?.replaceChildren();
  }
}

function formatMoney(value, currency = 'USD') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount);
  } catch {
    return '';
  }
}

function dataCountryName(code) {
  return window.ROAMUNO_COVERAGE?.countryNames?.[code] || code;
}

document.querySelectorAll('[data-regional-recommendations]').forEach((root) => new RoamUnoRegionalRecommendations(root));
